import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { fmtCents, fmtDateTime, TXN_LABELS, WALLET_LABELS } from "./util";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { MagnifyingGlass, DownloadSimple, CaretLeft, CaretRight, CaretDown, FilePdf, FileXls, FileCsv } from "@phosphor-icons/react";

const TYPES = ["all", ...Object.keys(TXN_LABELS)];
const WALLETS = ["all", "main", "staking", "referral"];
const inputCls = "rounded-lg border border-white/10 bg-[#08090F] px-3 py-2 text-sm text-white focus:border-[#2DD4BF]/50 focus:outline-none";

export default function Activities() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25 });
  const [type, setType] = useState("all");
  const [wallet, setWallet] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const load = useCallback(() => {
    const q = new URLSearchParams({ txn_type: type, wallet, page: String(page), limit: "25" });
    if (search) q.set("search", search);
    api.get(`/activities?${q}`).then((r) => setData(r.data)).catch(() => {});
  }, [type, wallet, search, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [type, wallet, search]);

  const filterLabel = () => {
    const parts = [];
    if (type !== "all") parts.push(`type: ${TXN_LABELS[type] || type}`);
    if (wallet !== "all") parts.push(`wallet: ${WALLET_LABELS[wallet] || wallet}`);
    if (search) parts.push(`search: "${search}"`);
    return parts.length ? parts.join(" · ") : "all records";
  };

  const fetchAllFiltered = async () => {
    const q = new URLSearchParams({ txn_type: type, wallet, page: "1", limit: "100000" });
    if (search) q.set("search", search);
    const r = await api.get(`/activities?${q}`);
    return r.data.items || [];
  };

  const withExport = (fn) => async () => {
    setExportOpen(false); setExporting(true);
    try {
      const items = await fetchAllFiltered();
      if (!items.length) { toast.error("No records match the current filters."); return; }
      fn(items);
    } catch { toast.error("Export failed. Please try again."); }
    finally { setExporting(false); }
  };

  const exportCsv = withExport((items) => {
    const rows = [["Date", "Transaction ID", "Type", "Wallet", "Amount (USD)", "Status", "Description"]];
    items.forEach((t) => rows.push([
      new Date(t.created_at).toISOString(), t.txn_id, TXN_LABELS[t.txn_type] || t.txn_type,
      WALLET_LABELS[t.wallet] || t.wallet, (t.amount_cents / 100).toFixed(2), t.status,
      `"${(t.description || "").replace(/"/g, "'")}"`,
    ]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `quantum-scalp-activities-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${items.length} records to CSV`);
  });

  const exportExcel = withExport((items) => {
    const rows = items.map((t) => ({
      Date: fmtDateTime(t.created_at), "Transaction ID": t.txn_id,
      Type: TXN_LABELS[t.txn_type] || t.txn_type, Wallet: WALLET_LABELS[t.wallet] || t.wallet,
      "Amount (USD)": Number((t.amount_cents / 100).toFixed(2)), Status: t.status, Description: t.description || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 20 }, { wch: 16 }, { wch: 22 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 40 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Activities");
    XLSX.writeFile(wb, `quantum-scalp-activities-${Date.now()}.xlsx`);
    toast.success(`Exported ${items.length} records to Excel`);
  });

  const exportPdf = withExport((items) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt" });
    doc.setFontSize(15); doc.setTextColor(20, 20, 30);
    doc.text("Quantum Scalp — Activity Ledger", 40, 40);
    doc.setFontSize(9); doc.setTextColor(120, 120, 130);
    doc.text(`Filters: ${filterLabel()}  ·  ${items.length} records  ·  Generated ${new Date().toLocaleString()}`, 40, 56);
    autoTable(doc, {
      startY: 68,
      head: [["Date", "Txn ID", "Type", "Wallet", "Amount (USD)", "Status"]],
      body: items.map((t) => [
        fmtDateTime(t.created_at), t.txn_id, TXN_LABELS[t.txn_type] || t.txn_type,
        WALLET_LABELS[t.wallet] || t.wallet, `${t.amount_cents >= 0 ? "+" : ""}${(t.amount_cents / 100).toFixed(2)}`, t.status,
      ]),
      styles: { fontSize: 8, cellPadding: 4, textColor: [40, 40, 50] },
      headStyles: { fillColor: [41, 98, 255], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [244, 247, 252] },
      margin: { left: 40, right: 40 },
    });
    doc.save(`quantum-scalp-activities-${Date.now()}.pdf`);
    toast.success(`Exported ${items.length} records to PDF`);
  });

  const pages = Math.max(1, Math.ceil(data.total / data.limit));

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input data-testid="act-search" className={`${inputCls} w-full pl-9`} placeholder="Search transaction ID or description…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select data-testid="act-type" className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
          {TYPES.map((t) => <option key={t} value={t} className="bg-[#08090F]">{t === "all" ? "All Types" : TXN_LABELS[t]}</option>)}
        </select>
        <select data-testid="act-wallet" className={inputCls} value={wallet} onChange={(e) => setWallet(e.target.value)}>
          {WALLETS.map((w) => <option key={w} value={w} className="bg-[#08090F]">{w === "all" ? "All Wallets" : WALLET_LABELS[w]}</option>)}
        </select>
        <div className="relative" ref={exportRef}>
          <button onClick={() => setExportOpen((v) => !v)} data-testid="act-export" disabled={exporting} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-white text-sm hover:bg-white/5 disabled:opacity-50">
            <DownloadSimple size={16} /> {exporting ? "Exporting…" : "Export"} <CaretDown size={13} className={`transition-transform ${exportOpen ? "rotate-180" : ""}`} />
          </button>
          {exportOpen && (
            <div className="absolute right-0 top-full mt-2 w-44 z-20 rounded-lg border border-white/10 bg-[#0A0B10] p-1.5 shadow-[0_16px_40px_rgba(0,0,0,0.55)]" data-testid="act-export-menu">
              <button onClick={exportPdf} data-testid="act-export-pdf" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-[#cbd5e1] hover:bg-white/5 hover:text-white"><FilePdf size={16} className="text-red-400" /> Export as PDF</button>
              <button onClick={exportExcel} data-testid="act-export-excel" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-[#cbd5e1] hover:bg-white/5 hover:text-white"><FileXls size={16} className="text-emerald-400" /> Export as Excel</button>
              <button onClick={exportCsv} data-testid="act-export-csv" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-[#cbd5e1] hover:bg-white/5 hover:text-white"><FileCsv size={16} className="text-[#2DD4BF]" /> Export as CSV</button>
              <p className="px-3 py-1.5 text-[10px] text-[#64748B] font-mono border-t border-white/5 mt-1">Applies current filters</p>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0A0B10] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#64748B] border-b border-white/10">
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Txn ID</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Wallet</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Status</th>
            </tr></thead>
            <tbody data-testid="act-table">
              {data.items.length ? data.items.map((t) => (
                <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-[#94A3B8] whitespace-nowrap">{fmtDateTime(t.created_at)}</td>
                  <td className="px-4 py-3"><code className="font-mono text-xs text-[#2DD4BF]">{t.txn_id}</code></td>
                  <td className="px-4 py-3 text-white">{TXN_LABELS[t.txn_type] || t.txn_type}</td>
                  <td className="px-4 py-3 text-[#94A3B8]">{WALLET_LABELS[t.wallet] || t.wallet}</td>
                  <td className={`px-4 py-3 text-right font-mono ${t.amount_cents >= 0 ? "text-emerald-300" : "text-red-300"}`}>{t.amount_cents >= 0 ? "+" : ""}{fmtCents(t.amount_cents)}</td>
                  <td className="px-4 py-3"><span className="font-mono text-[10px] uppercase text-[#64748B]">{t.status}</span></td>
                </tr>
              )) : <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#64748B] font-mono">No transactions match your filters.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-sm">
          <span className="text-[#64748B]">{data.total} records</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="grid place-items-center h-8 w-8 rounded-md border border-white/10 text-[#94A3B8] disabled:opacity-40 hover:text-white"><CaretLeft size={15} /></button>
            <span className="font-mono text-xs text-[#cbd5e1]">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="grid place-items-center h-8 w-8 rounded-md border border-white/10 text-[#94A3B8] disabled:opacity-40 hover:text-white"><CaretRight size={15} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
